import { find, findAll } from "./dom";

/** The word shown beside the stars for each rating; index 0 is "nothing chosen yet". */
const WORDS = ["", "Dåligt", "Mindre bra", "Bra", "Mycket bra", "Utmärkt"];

/** The fragment that opens the folded form, so a mail or another page can link straight to it. */
const DEEP_LINK = "#write-review";

/** Returns an input's trimmed value, or an empty string when the input is missing. */
function value(form: HTMLFormElement, id: string): string {
  return find<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(`#${id}`, form)?.value.trim() ?? "";
}

/** Returns whether a checkbox in the form is ticked. */
function checked(form: HTMLFormElement, id: string): boolean {
  return find<HTMLInputElement>(`#${id}`, form)?.checked ?? false;
}

/**
 * The public review form's body, as the endpoint binds it.
 *
 * <p>The accommodation and category come from the form's data attributes on an accommodation page,
 * where the form is about that one place, and from the two selects on the review form page. An
 * anonymous guest's name is not sent at all.</p>
 */
export function reviewPayload(form: HTMLFormElement): Record<string, unknown> {
  const rating = find<HTMLInputElement>('input[name="review-rating"]:checked', form);
  const anonymous = checked(form, "review-anonymous");
  const category = value(form, "review-category") || form.dataset["reviewCategory"] || "";
  const accommodation = category === "accommodation" ? value(form, "review-accommodation") || form.dataset["reviewAccommodation"] || "" : "";
  return {
    rating: rating === null ? 0 : Number(rating.value),
    text: value(form, "review-text"),
    name: anonymous ? "" : value(form, "review-name"),
    anonymous,
    email: value(form, "review-email"),
    consentToPublish: checked(form, "review-consent"),
    category,
    accommodation: accommodation === "" ? null : accommodation,
    website: value(form, "review-website"),
  };
}

/** Writes or clears the error beside one of the form's choices. */
function setChoiceError(form: HTMLFormElement, name: string, message: string): void {
  const box = find<HTMLElement>(`[data-error-for="${name}"]`, form);
  if (box !== null) {
    box.textContent = message;
  }
}

/**
 * Checks what the text fields cannot: that a star is chosen, and on the review form page that the
 * review says what it is about.
 *
 * @returns The first control in error, for focus, or null.
 */
export function checkReviewChoices(form: HTMLFormElement): HTMLElement | null {
  let first: HTMLElement | null = null;

  setChoiceError(form, "review-rating", "");
  if (find<HTMLInputElement>('input[name="review-rating"]:checked', form) === null) {
    setChoiceError(form, "review-rating", "Välj hur många stjärnor vistelsen får.");
    first = find<HTMLInputElement>('input[name="review-rating"]', form);
  }

  const category = find<HTMLSelectElement>("#review-category", form);
  if (category !== null) {
    setChoiceError(form, "review-category", "");
    if (category.value === "") {
      setChoiceError(form, "review-category", "Välj vad omdömet gäller.");
      first ??= category;
    }
  }

  const accommodation = find<HTMLSelectElement>("#review-accommodation", form);
  if (accommodation !== null && !accommodation.disabled) {
    setChoiceError(form, "review-accommodation", "");
    if (accommodation.value === "") {
      setChoiceError(form, "review-accommodation", "Välj vilket boende omdömet gäller.");
      first ??= accommodation;
    }
  }

  return first;
}

/** Shows the word for the chosen rating beside the stars, where a screen reader also hears it. */
function wireRatingWord(form: HTMLFormElement): void {
  const word = find<HTMLElement>("[data-rating-word]", form);
  if (word === null) {
    return;
  }
  form.addEventListener("change", (event: Event) => {
    const target = event.target;
    if (target instanceof HTMLInputElement && target.name === "review-rating") {
      word.textContent = WORDS[Number(target.value)] ?? "";
      setChoiceError(form, "review-rating", "");
    }
  });
}

/**
 * Hides a field and takes it out of the form when a choice makes it irrelevant.
 *
 * <p>Disabled as well as hidden: a disabled field is neither validated nor sent, so a name typed
 * before the guest chose to be anonymous never leaves the browser.</p>
 */
function toggleField(wrapper: HTMLElement | null, control: HTMLInputElement | HTMLSelectElement | null, show: boolean): void {
  if (wrapper === null || control === null) {
    return;
  }
  wrapper.hidden = !show;
  control.disabled = !show;
  if (!show) {
    control.removeAttribute("aria-invalid");
  }
}

/** Wires the choices that change the form: anonymity, the category and consent. */
function wireChoices(form: HTMLFormElement): void {
  const anonymous = find<HTMLInputElement>("#review-anonymous", form);
  const nameField = find<HTMLElement>("[data-name-field]", form);
  const name = find<HTMLInputElement>("#review-name", form);
  const syncName = (): void => {
    toggleField(nameField, name, !(anonymous?.checked ?? false));
    if (anonymous?.checked === true) {
      setChoiceError(form, "review-name", "");
    }
  };
  anonymous?.addEventListener("change", syncName);
  syncName();

  const category = find<HTMLSelectElement>("#review-category", form);
  const accommodationField = find<HTMLElement>("[data-accommodation-field]", form);
  const accommodation = find<HTMLSelectElement>("#review-accommodation", form);
  if (category !== null) {
    const syncAccommodation = (): void => toggleField(accommodationField, accommodation, category.value === "accommodation");
    category.addEventListener("change", syncAccommodation);
    syncAccommodation();
  }

  // The receipt's last line depends on the guest's answer, so it is written when they give it.
  const consent = find<HTMLInputElement>("#review-consent", form);
  const syncNote = (): void => {
    form.dataset["doneNote"] = consent?.checked === true
      ? "Omdömet publiceras när vi har läst det, och vi mejlar dig när det syns."
      : "Omdömet stannar hos oss och publiceras inte.";
  };
  consent?.addEventListener("change", syncNote);
  syncNote();
}

/**
 * Opens a folded form when the page is reached through its deep link, and prepares it the first
 * time it opens.
 */
function wireFold(form: HTMLFormElement, prepare: (form: HTMLFormElement) => Promise<void>): void {
  const fold = form.closest("details");
  if (fold === null) {
    void prepare(form);
    return;
  }

  fold.addEventListener("toggle", () => {
    if (fold.open) {
      void prepare(form);
    }
  });

  const openFromLink = (): void => {
    if (location.hash === DEEP_LINK) {
      fold.open = true;
      fold.scrollIntoView({ block: "start" });
    }
  };
  window.addEventListener("hashchange", openFromLink);
  openFromLink();
}

/**
 * Wires every public review form on the page.
 *
 * @param prepare Fetches the form's token and enables its fields, once; handed in by the form
 *   module so the two do not import each other.
 */
export function initReviewForms(prepare: (form: HTMLFormElement) => Promise<void>): void {
  for (const form of findAll<HTMLFormElement>("[data-review-public]")) {
    wireRatingWord(form);
    wireChoices(form);
    wireFold(form, prepare);
  }
}
